// =============================================================================
// auditLog
// =============================================================================
// PURPOSE: Client-side audit logging for security-relevant actions.
//
// ARCHITECTURE (revived 2026-07-07 — was a 5-month no-op posting to a dead N8N webhook):
// - Batches events in memory, flushes DIRECT to Supabase every 5 seconds via the
//   SECURITY DEFINER RPC `log_audit_events` (doctrine: no N8N write gateway).
// - Auto-flush on page unload (beforeunload/pagehide), keepalive fetch.
// - Fire-and-forget: audit logging must NEVER break user experience (silent fail).
//
// TAMPER MODEL:
// - The RPC server-stamps user_id (auth.uid()) + created_at (now()); a client can
//   never forge who/when. organization_id is captured PER-EVENT at enqueue (the org
//   on screen when the action happened) and re-validated server-side.
// - login/logout carry organization_id = null (lifecycle events, no active org).
//
// KEY DECISIONS:
// - Per-event org (not per-batch): an org-switch mid-batch must not mis-attribute
//   the whole batch. Org read from localStorage['altara_last_org_id'] at enqueue.
// - Self-subscribed token: we listen to supabase.auth.onAuthStateChange directly so
//   we see TOKEN_REFRESHED (AuthContext's handler early-returns on it → would stale
//   the token and 401 every flush). Module-level, cycle-free.
// - client_event_id (uuid) per event: in-batch dedup key + forensic trace.
// - MAX_EVENTS_PER_FLUSH cap keeps unload bodies under the 64 KB keepalive ceiling.
// =============================================================================

import { supabase } from "./supabase";

interface AuditEvent {
  action: string;
  resource_type?: string;
  resource_id?: string;
  metadata?: Record<string, any>;
  timestamp: number;
  user_agent: string;
  client_event_id: string;
  organization_id: string | null;
}

// Event batch queue
let eventQueue: AuditEvent[] = [];
let batchTimer: ReturnType<typeof setTimeout> | null = null;
const BATCH_INTERVAL_MS = 5000; // Send every 5 seconds
const MAX_EVENTS_PER_FLUSH = 100; // keep keepalive body under the 64 KB unload ceiling

// Auth token cache — self-subscribed so we catch TOKEN_REFRESHED (not just SIGNED_IN).
// AuthContext's own handler early-returns on TOKEN_REFRESHED, so pushing from there
// would stale this token and 401 every flush. Subscribing here is cycle-free
// (supabase.ts does not import auditLog).
let cachedToken: string | null = null;
if (typeof window !== "undefined" && supabase) {
  supabase.auth.getSession().then(({ data }) => {
    cachedToken = data.session?.access_token ?? null;
  });
  supabase.auth.onAuthStateChange((_event, session) => {
    cachedToken = session?.access_token ?? null;
  });
}

/**
 * Flush batched events DIRECT to Supabase via the log_audit_events RPC.
 * Drains the queue synchronously BEFORE any async work (concurrent-flush safe),
 * then fires a keepalive fetch. Fire-and-forget: never throws into the UI.
 */
const flushEvents = (): void => {
  // Drain synchronously — no await before eventQueue is reset (concurrency invariant).
  let batch = eventQueue;
  eventQueue = [];
  if (batch.length === 0) return;

  // Token not ready yet → requeue and wait for the next flush.
  if (!cachedToken) {
    eventQueue = batch.concat(eventQueue);
    return;
  }

  // Cap the batch so an unload body stays under the keepalive ceiling; requeue the rest.
  if (batch.length > MAX_EVENTS_PER_FLUSH) {
    eventQueue = batch.slice(MAX_EVENTS_PER_FLUSH).concat(eventQueue);
    batch = batch.slice(0, MAX_EVENTS_PER_FLUSH);
  }

  fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/log_audit_events`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // PostgREST requires the apikey header in addition to the user Bearer token.
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${cachedToken}`,
    },
    body: JSON.stringify({ p_events: batch }), // org is per-event inside each element
    keepalive: true,
  }).catch(() => {
    // Silent fail — audit logging must not break user experience.
  });
};

/**
 * Schedule a batch send.
 */
const scheduleBatch = () => {
  if (batchTimer) return; // Already scheduled

  batchTimer = setTimeout(() => {
    batchTimer = null;
    flushEvents();
    // flushEvents() requeues when the token isn't ready yet, or when a batch exceeded
    // MAX_EVENTS_PER_FLUSH. Reschedule so the remainder drains instead of stranding until
    // page unload. The poll resolves as soon as the token arrives / the queue empties.
    if (eventQueue.length > 0) scheduleBatch();
  }, BATCH_INTERVAL_MS);
};

/**
 * Log an audit event.
 *
 * @param action - Action type: 'login', 'logout', 'create', 'update', 'delete', 'export', 'view_sensitive'
 * @param resourceType - Resource type: 'transaction', 'lead', 'template', etc.
 * @param resourceId - ID of resource (optional)
 * @param metadata - Additional context (optional)
 */
export const logAudit = (
  action: string,
  resourceType?: string,
  resourceId?: string,
  metadata?: Record<string, any>
) => {
  // Org captured PER-EVENT at enqueue (the org on screen now). login/logout are
  // lifecycle events with no active org → null (reading a stale org here would
  // mis-attribute the login to the prior session's org).
  const organization_id =
    action === "login" || action === "logout"
      ? null
      : localStorage.getItem("altara_last_org_id") || null;

  const event: AuditEvent = {
    action,
    resource_type: resourceType,
    resource_id: resourceId,
    metadata,
    timestamp: Date.now(),
    user_agent: navigator.userAgent,
    client_event_id: crypto.randomUUID(),
    organization_id,
  };

  eventQueue.push(event);
  scheduleBatch();
};

/**
 * Flush events immediately (e.g., on page unload).
 */
export const flushAuditLogs = () => {
  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }
  flushEvents();
};

// Flush on page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", flushAuditLogs);
  window.addEventListener("pagehide", flushAuditLogs);
}
